import { Spinner } from "@mixitone/components";
import { AccountUser, IncidentLog } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import dayjs from "dayjs";
import { FormController } from "./FormController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import ModalFormRenderer from "./FormRenderers/ModalFormRenderer";

class DateTime {
  private dayjs: dayjs.Dayjs;

  constructor(date: Date) {
    this.dayjs = dayjs(date);
  }

  get date() {
    return this.dayjs.format("YYYY-MM-DD");
  }
  set date(value: string) {
    this.dayjs = dayjs(`${value}T${this.time}`);
  }

  get time() {
    return this.dayjs.format("HH:mm");
  }
  set time(value: string) {
    this.dayjs = dayjs(`${this.date}T${value}`);
  }

  get value() {
    return this.dayjs.toDate();
  }
}

class IncidentLogFormController extends FormController<
  IncidentLog,
  { night_id: string },
  {
    incident_occurred_at: DateTime;
  }
> {
  override findModel(id: string) {
    return IncidentLog.query().find(id);
  }
  override async buildRecord(): Promise<IncidentLog> {
    this.state.incident_occurred_at = new DateTime(new Date());

    const log = new IncidentLog({
      night_id: this.state.attributes.night_id,
      logged_by_account_user_id: AccountUser.current?.id ?? "",
      account_id: AccountUser.current?.account_id ?? "",
      incident_description: "",
    });
    log.incident_occurred_at = new Date();

    return log;
  }

  override async onLoad(): Promise<void> {
    this.state.incident_occurred_at = new DateTime(this.state.record.incident_occurred_at || new Date());
  }

  fields(): Record<string, FormFieldConfig> {
    return {
      incident_description: {
        name: "incident_description",
        label: "Incident description",
        type: "text",
        required: true,
        placeholder: "Please describe the incident",
      },
      incident_occurred_at_date: {
        name: "incident_occurred_at_date",
        label: "Date of incident",
        type: "datetime",
        includeTime: false,
        required: true,
      },
      incident_occurred_at_time: {
        name: "incident_occurred_at_time",
        label: "Approximate time of incident",
        type: "text",
        required: true,
      },
    };
  }

  override saveRecord(record: IncidentLog): Promise<void> {
    record.night_id = this.state.attributes.night_id;
    record.incident_occurred_at = this.state.incident_occurred_at.value;
    return record.save();
  }
}

const IncidentLogForm: React.FC = () => {
  const controller = IncidentLogFormController.use();
  const { loading } = controller.state;

  return loading ? (
    <Spinner size={64} />
  ) : (
    <ModalFormRenderer fields={Object.values(controller.fields())} name="Incident log" showDelete={false} />
  );
};

export default IncidentLogFormController.scope(ApplicationView(IncidentLogForm));
